import React, { useState, useEffect } from 'react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  userEmail: string;
}

type SubmitState = 'idle' | 'sending' | 'success' | 'error';

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, userName, userEmail }) => {
  const [message, setMessage] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Auto-close after success
  useEffect(() => {
    if (submitState === 'success') {
      const timer = setTimeout(() => {
        handleClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitState]);

  const handleClose = () => {
    setMessage('');
    setSubmitState('idle');
    setErrorMessage('');
    onClose();
  };

  const handleSubmit = async () => {
    if (message.trim().length < 10) return;

    setSubmitState('sending');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const response = await fetch(`${apiBase}/admin/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ name: userName, email: userEmail, message: message.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitState('success');
      } else {
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
        setSubmitState('error');
      }
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      setSubmitState('error');
    }
  };

  if (!isOpen) return null;

  const isSending = submitState === 'sending';
  const isSuccess = submitState === 'success';
  const isError = submitState === 'error';
  const messageValid = message.trim().length >= 10;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-[4px_4px_0px_#594A42] max-w-md w-full">
        <h3 className="text-coffee font-semibold text-lg mb-4">Contact ShiYou Team</h3>

        {/* Name (read-only) */}
        <div className="mb-3">
          <label className="text-coffee/80 font-medium text-sm mb-1 block">Name</label>
          <input
            type="text"
            value={userName}
            readOnly
            className="w-full border-2 border-coffee rounded-lg px-3 py-2 bg-paper opacity-70 cursor-not-allowed text-coffee"
          />
        </div>

        {/* Email (read-only) */}
        <div className="mb-3">
          <label className="text-coffee/80 font-medium text-sm mb-1 block">Email</label>
          <input
            type="email"
            value={userEmail}
            readOnly
            className="w-full border-2 border-coffee rounded-lg px-3 py-2 bg-paper opacity-70 cursor-not-allowed text-coffee"
          />
        </div>

        {/* Message textarea */}
        <div className="mb-4">
          <label className="text-coffee/80 font-medium text-sm mb-1 block">Message</label>
          <textarea
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (isError) { setSubmitState('idle'); setErrorMessage(''); }
            }}
            placeholder="Type your question, feedback, or idea... (min 10 characters)"
            disabled={isSending || isSuccess}
            rows={4}
            className="w-full border-2 border-coffee rounded-lg px-3 py-2 bg-white text-coffee placeholder-coffee/40 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta resize-none disabled:opacity-60"
          />
          <p className="text-xs text-coffee/60 mt-1 text-right">
            {message.trim().length} chars{message.trim().length < 10 ? ` (${10 - message.trim().length} more needed)` : ''}
          </p>
        </div>

        {/* Success message */}
        {isSuccess && (
          <p className="text-green-700 font-semibold text-sm mb-4 text-center">
            Message sent! ✓ Closing...
          </p>
        )}

        {/* Error message */}
        {isError && errorMessage && (
          <p className="text-red-600 text-sm mb-4">{errorMessage}</p>
        )}

        {/* Buttons */}
        <div className="flex space-x-2">
          <button
            onClick={handleClose}
            disabled={isSending}
            className="flex-1 bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSending || isSuccess || !messageValid}
            className="flex-1 bg-terracotta text-white border-2 border-coffee px-4 py-2 rounded-lg hover:bg-[#d89050] transition-colors disabled:opacity-50"
          >
            {isSending ? 'Sending...' : isSuccess ? 'Sent!' : 'Send Message'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
